import { clusterCountStyle, clusterStyle, orderStyle } from './style'
import {
    mapCameraAnimationDuration,
    mapClusterMaxZoom,
    mapMaxZoom,
} from '@/features/orders/components/MapPanel/parts/Content/constants'
import {
    mapClusterRadius,
    mapLayerIds,
    mapSourceIds,
} from '@/features/orders/components/MapPanel/parts/Content/parts/Map/constants/layerConfig'
import {
    pinIconOffset,
    pinLabelFont,
    pinLabelTextOffset,
    pinLabelTextSize,
} from '@/features/orders/components/MapPanel/parts/Content/parts/Map/constants/pinStyle'
import { MapProps } from '@/features/orders/components/MapPanel/parts/Content/parts/Map/types'
import {
    MapCoordinate,
    OrderMapPoint,
} from '@/features/orders/components/MapPanel/parts/Content/types'
import { getTopOrderId } from '@/features/orders/components/MapPanel/parts/Content/utils/getTopOrderId'
import { toMapFeatures } from '@/features/orders/components/MapPanel/parts/Content/utils/toMapFeatures'
import { logger } from '@/features/orders/logger'
import { useLatestValueRef } from '@/hooks/useLatestValueRef'
import type mapboxgl from 'mapbox-gl'
import { useEffect, useMemo, useRef, useState } from 'react'

interface UseOrdersLayerOptions extends Pick<MapProps, 'paused' | 'onOrderPress' | 'onError'> {
    map: mapboxgl.Map | undefined
    orders: OrderMapPoint[]
}

export const useOrdersLayer = ({
    map,
    orders,
    paused,
    onOrderPress,
    onError,
}: UseOrdersLayerOptions) => {
    const callbacks = useLatestValueRef({ onOrderPress, onError })
    const shape = useMemo(() => toMapFeatures(orders), [orders])
    const applied = useRef<{ map: mapboxgl.Map; shape: typeof shape } | undefined>(undefined)
    const [readyMap, setReadyMap] = useState<mapboxgl.Map | undefined>(undefined)

    useEffect(() => {
        if (!map) return

        let disposed = false

        try {
            map.addSource(mapSourceIds.orders, {
                type: 'geojson',
                data: toMapFeatures([]),
                cluster: true,
                clusterRadius: mapClusterRadius,
                clusterMaxZoom: mapClusterMaxZoom,
            })
            map.addLayer({
                id: mapLayerIds.orderClusters,
                type: 'circle',
                source: mapSourceIds.orders,
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': clusterStyle.color,
                    'circle-radius': clusterStyle.radius,
                    'circle-stroke-color': clusterStyle.strokeColor,
                    'circle-stroke-width': clusterStyle.strokeWidth,
                },
            })
            map.addLayer({
                id: mapLayerIds.orderClusterCount,
                type: 'symbol',
                source: mapSourceIds.orders,
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': clusterCountStyle.textField,
                    'text-size': clusterCountStyle.textSize,
                    'text-font': [pinLabelFont],
                    'text-allow-overlap': clusterCountStyle.allowOverlap,
                },
                paint: { 'text-color': clusterCountStyle.textColor },
            })
            map.addLayer({
                id: mapLayerIds.orderPins,
                type: 'symbol',
                source: mapSourceIds.orders,
                filter: ['!', ['has', 'point_count']],
                layout: {
                    'icon-image': orderStyle.iconImage,
                    'icon-anchor': orderStyle.iconAnchor,
                    'icon-offset': pinIconOffset,
                    'icon-allow-overlap': orderStyle.allowOverlap,
                    'text-field': orderStyle.textField,
                    'text-font': [pinLabelFont],
                    'text-size': pinLabelTextSize,
                    'text-anchor': orderStyle.textAnchor,
                    'text-offset': pinLabelTextOffset,
                    'text-allow-overlap': orderStyle.allowOverlap,
                    'symbol-sort-key': orderStyle.sortKey,
                },
                paint: { 'text-color': orderStyle.textColor },
            })

            map.on('click', mapLayerIds.orderPins, (event) => {
                const orderId = getTopOrderId(event.features ?? [])
                if (orderId) callbacks.current.onOrderPress(orderId)
            })
            map.on('click', mapLayerIds.orderClusters, (event) => {
                const feature = event.features?.[0]
                const clusterId = feature?.properties?.cluster_id
                if (typeof clusterId !== 'number' || feature?.geometry.type !== 'Point') return

                const [longitude, latitude] = feature.geometry.coordinates
                if (typeof longitude !== 'number' || typeof latitude !== 'number') return

                const center: MapCoordinate = [longitude, latitude]
                const source = map.getSource<mapboxgl.GeoJSONSource>(mapSourceIds.orders)

                if (!source) return

                source.getClusterExpansionZoom(clusterId, (error, zoom) => {
                    if (disposed) return

                    if (error || zoom == null) {
                        logger.errorOnly(error ?? new Error('Could not expand orders map cluster'))

                        return
                    }

                    map.easeTo({
                        center,
                        zoom: Math.min(zoom, mapMaxZoom),
                        duration: mapCameraAnimationDuration,
                    })
                })
            })
            for (const layer of [mapLayerIds.orderPins, mapLayerIds.orderClusters]) {
                map.on('mouseenter', layer, () => {
                    map.getCanvas().style.cursor = 'pointer'
                })
                map.on('mouseleave', layer, () => {
                    map.getCanvas().style.cursor = ''
                })
            }
            setReadyMap(map)
        } catch (error) {
            callbacks.current.onError(error)
        }

        return () => {
            disposed = true
        }
    }, [callbacks, map])

    useEffect(() => {
        if (!map || paused) return
        if (applied.current?.map === map && applied.current.shape === shape) return

        const source = map.getSource<mapboxgl.GeoJSONSource>(mapSourceIds.orders)
        if (!source) return

        source.setData(shape)
        applied.current = { map, shape }
    }, [map, paused, shape])

    return !!map && readyMap === map
}
